import { useEffect, useMemo, useState } from 'react';
import { Skeleton, Spin, message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';

import NoData from 'components/NoData';
import { useLanguageContext } from 'contexts/Localization';
import useTranslation from 'customHooks/translate';
import { ACCOUNT_INFO, MAIN_USER_PACKAGE_PLANS } from 'events/types';
import { mainUserPlans } from 'pricing';
import {
  checkout,
  getPackagePlansForMainUsers,
  MainUserPackagePlan,
} from 'services/payment';
import { getAccountData } from 'services/accounts';
import { toastError } from 'services/helpers/getError';
import { ERefetchType } from 'enums';
import { filterPaidPackagePlans, openCheckoutPaymentInNewTab } from 'utils';
import {
  DEFAULT_PAYMENT_CURRENCY_CODE,
  DEFAULT_PAYMENT_CURRENCY_TEXT,
} from 'consts';

const getLandingPlanMeta = (plan: MainUserPackagePlan) =>
  mainUserPlans.find((landingPlan) => landingPlan.price === plan.price);

const MainUserPricingSection = () => {
  const translation = useTranslation();
  // @ts-ignore
  const { language } = useLanguageContext();
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [activeSubscriptionPackageId, setActiveSubscriptionPackageId] =
    useState<number | null>(null);

  const { data: accountInfo } = useQuery({
    queryKey: [ACCOUNT_INFO],
    queryFn: getAccountData,
    select: (response) => response.data.data,
  });

  const expiryDate = accountInfo?.subscriptionExpiration;

  useEffect(() => {
    if (accountInfo?.activeSubscriptionPackageId == null) return;

    const activeId = Number(accountInfo.activeSubscriptionPackageId);
    setActiveSubscriptionPackageId(activeId);
    setSelectedPlanId((current) => current ?? activeId);
  }, [accountInfo?.activeSubscriptionPackageId]);

  const plansQuery = useQuery<MainUserPackagePlan[]>({
    queryKey: [MAIN_USER_PACKAGE_PLANS],
    queryFn: async () => {
      const response = await getPackagePlansForMainUsers();
      return response.data.data ?? [];
    },
    refetchOnMount: ERefetchType.ALWAYS,
    staleTime: 0,
  });

  const plans = filterPaidPackagePlans(plansQuery.data ?? []);

  const selectedPlan = useMemo(
    () => plans.find((plan) => Number(plan.id) === selectedPlanId) ?? null,
    [plans, selectedPlanId]
  );

  const checkoutMutation = useMutation({
    mutationFn: checkout,
    onSuccess: (response) => {
      if (!openCheckoutPaymentInNewTab(response)) {
        message.error(translation.profile.paymentPageOpenFailed);
      }
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const showUpgradeNotice = useMemo(
    () => plans?.some((user) => user?.isUpgradeEnabled === false),
    [plans]
  );

  const handleCheckout = () => {
    if (!selectedPlan) {
      message.warning(translation.profile.selectPlan);
      return;
    }

    checkoutMutation.mutate({
      amount: selectedPlan.price,
      currencyCode: DEFAULT_PAYMENT_CURRENCY_CODE,
      currencyText: DEFAULT_PAYMENT_CURRENCY_TEXT,
      userSubscriptionDetails: [
        {
          userId: selectedPlan.mainUserId,
          packagePlanId: Number(selectedPlan.id),
        },
      ],
    });
  };

  if (plansQuery.isLoading) {
    return (
      <div className="grid grid-cols-1 gap-[16px] md:grid-cols-3 md:gap-[20px]">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} active paragraph={{ rows: 4 }} />
        ))}
      </div>
    );
  }

  if (!plans.length) {
    return <NoData />;
  }

  return (
    <Spin spinning={checkoutMutation.isLoading}>
      <div className="flex flex-col gap-[24px]">
        {expiryDate && (
          <span className="inline-flex w-fit items-center rounded-[6px] bg-[rgba(70,106,251,0.08)] px-[10px] py-[4px] text-[12px] text-[rgba(41,45,50,0.75)]">
            {translation.profile.subscriptionExpiryDate}:{' '}
            <span className="ml-[4px] font-semibold tabular-nums text-[#466AFB]">
              {expiryDate}
            </span>
          </span>
        )}
        {showUpgradeNotice && (
          <div className="rounded-[12px] border border-[rgba(70,106,251,0.2)] bg-[rgba(70,106,251,0.06)] px-[16px] py-[12px] text-[14px] leading-relaxed text-[rgba(41,45,50,0.85)]">
            {translation.profile.mainUserUpgradeNotAllowed}
          </div>
        )}
        <div className="grid grid-cols-1 gap-[16px] md:grid-cols-2 md:gap-[20px] lg:grid-cols-3">
          {plans.map((plan) => {
            const planId = Number(plan.id);
            const isSelected = selectedPlanId === planId;
            const isActivePackage =
              activeSubscriptionPackageId != null &&
              planId === activeSubscriptionPackageId;
            const landingPlan = getLandingPlanMeta(plan);
            const features = landingPlan?.features[language] ?? [];
            return (
              <div
                key={plan.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedPlanId(planId)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedPlanId(planId);
                  }
                }}
                className={`relative flex cursor-pointer flex-col gap-5 overflow-hidden rounded-[12px] border p-[16px] transition-all duration-200 md:gap-6 md:p-[32px] ${
                  isSelected
                    ? 'border-[#3451C7] bg-[rgba(70,106,251,0.04)]'
                    : 'border-[rgba(27,27,31,0.12)] bg-white hover:border-[rgba(70,106,251,0.35)] hover:bg-[rgba(70,106,251,0.02)]'
                }`}
              >
                {isActivePackage && (
                  <span className="absolute right-0 top-0 rounded-bl-[10px] bg-[#6DCD90] px-[12px] py-[6px] text-[11px] font-semibold tracking-wide text-white shadow-sm">
                    {translation.profile.activePackage}
                  </span>
                )}
                <div className="flex flex-col flex-wrap items-baseline gap-[6px] md:gap-[12px]">
                  <h4 className="text-lg font-bold tracking-tight text-ultramarine-blue md:text-xl">
                    {landingPlan?.duration} {translation.reports.Months}
                  </h4>
                  <div className="flex flex-wrap items-center gap-[12px]">
                    <span className="text-2xl font-bold tabular-nums text-eerie-black md:text-3xl">
                      {plan.price} {plan.packageCurrencyId}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-charleston-green/80 md:text-base">
                    {landingPlan?.pricePerMonth} {plan.packageCurrencyId} /{' '}
                    {translation.reports.Month}
                  </span>
                </div>

                <div className="h-[1px] w-full bg-[rgba(41,45,50,0.1)]" />

                {features.length > 0 && (
                  <ul className="space-y-3">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-[4px]">
                        <img
                          src="assets/tick-circle.svg"
                          alt={feature}
                          className="h-4 w-4 shrink-0"
                        />
                        <span className="text-sm font-medium leading-relaxed text-charleston-green/90 md:text-[15px]">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        {!showUpgradeNotice && (
          <button
            type="button"
            onClick={handleCheckout}
            disabled={checkoutMutation.isLoading || !selectedPlan}
            className="w-fit rounded-[8px] bg-green-600 px-[24px] py-[10px] text-[14px] font-semibold text-white transition-all hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {translation.profile.pay}
          </button>
        )}
      </div>
    </Spin>
  );
};

export default MainUserPricingSection;
